import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Loader2, Trash2, RefreshCw, Phone, Mail, FileDown, Save, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ReviewsAdmin } from "@/components/ReviewsAdmin";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_STYLE = {
  pending: "bg-hazard/20 text-hazard border-hazard/40",
  confirmed: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  completed: "bg-green-500/20 text-green-300 border-green-500/40",
  cancelled: "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("bookings");
  const [reviews, setReviews] = useState([]);
  const [ownerNotes, setOwnerNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const pendingReviews = reviews.filter((r) => r.status === "pending").length;

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const [b, s, rv] = await Promise.all([
        api.get("/bookings"),
        api.get("/bookings/stats"),
        api.get("/reviews/admin"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setReviews(rv.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id, status) => {
    if (status === "completed") {
      toast.error("Completed jobs are kept permanently and can't be deleted.");
      return;
    }
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const saveOwnerNotes = async (id) => {
    setSavingNotes(id);
    try {
      await api.patch(`/bookings/${id}/owner-notes`, { owner_notes: ownerNotes[id] ?? "" });
      toast.success("Note saved");
      load();
    } catch {
      toast.error("Could not save note");
    } finally {
      setSavingNotes(null);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated");
      setShowChangePw(false);
      setCurPw("");
      setNewPw("");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not change password");
    } finally {
      setChangingPw(false);
    }
  };

  const exportCompleted = async () => {
    setExporting(true);
    try {
      const res = await api.get("/bookings/export/completed", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().split("T")[0];
      a.download = `warnrs-completed-jobs-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Completed jobs exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const reviewAction = async (id, status) => {
    try {
      await api.patch(`/reviews/${id}`, { status });
      toast.success(status === "approved" ? "Review published" : "Review hidden");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  if (user === null) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-hazard" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <div className="hazard-stripes h-2 w-full" />
      {/* header */}
      <header className="border-b border-surface-2 sticky top-0 bg-black/90 backdrop-blur-md z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 leading-none">
            <img src="/warnrs-w.png" alt="WARNRS" className="h-9 md:h-10 w-auto" data-testid="dashboard-w-logo" />
            <div className="flex flex-col">
              <span className="font-display text-2xl tracking-wide">DASHBOARD</span>
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                {user?.email}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="refresh-btn"
              className="p-3 border border-surface-2 hover:border-hazard text-zinc-400 hover:text-hazard transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => setShowChangePw(true)}
              data-testid="change-password-btn"
              className="hidden sm:flex items-center gap-2 border border-surface-2 px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-hazard hover:border-hazard transition-colors"
            >
              <KeyRound size={14} /> Password
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-2 border border-surface-2 px-5 py-3 font-mono text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-hazard hover:border-hazard transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {showChangePw && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-5" data-testid="change-password-modal">
          <div className="bg-ink border border-surface-2 w-full max-w-md p-6 md:p-8 relative">
            <button onClick={() => setShowChangePw(false)} data-testid="close-change-password" className="absolute top-4 right-4 text-zinc-500 hover:text-hazard" aria-label="Close">
              <X size={20} />
            </button>
            <h2 className="font-display uppercase text-3xl mb-1 flex items-center gap-2">
              <KeyRound size={22} className="text-hazard" /> Change Password
            </h2>
            <p className="text-zinc-500 font-mono text-[11px] tracking-[0.15em] uppercase mb-6">Update your dashboard password</p>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">Current Password</label>
                <input type="password" data-testid="current-password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required
                  className="w-full bg-surface border border-surface-2 px-4 py-3 text-white focus:outline-none focus:border-hazard" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2">New Password (min 8 chars)</label>
                <input type="password" data-testid="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} required
                  className="w-full bg-surface border border-surface-2 px-4 py-3 text-white focus:outline-none focus:border-hazard" />
              </div>
              <button type="submit" disabled={changingPw} data-testid="submit-change-password"
                className="w-full flex items-center justify-center gap-2 bg-hazard text-black py-3 font-mono text-sm tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-40">
                {changingPw ? <Loader2 size={16} className="animate-spin" /> : "Update Password"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-surface-2 border border-surface-2 mb-10">
          {[
            { label: "Total", value: stats.total },
            { label: "Pending", value: stats.pending },
            { label: "Confirmed", value: stats.confirmed },
            { label: "Completed", value: stats.completed },
          ].map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className="bg-ink p-6 md:p-8">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">
                {s.label}
              </div>
              <div className="font-display text-5xl md:text-6xl text-hazard leading-none">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* tab switcher */}
        <div className="flex gap-2 mb-8 border-b border-surface-2">
          {[
            ["bookings", "Bookings"],
            ["reviews", `Reviews${pendingReviews ? ` (${pendingReviews})` : ""}`],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              className={`px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors ${
                tab === id ? "border-hazard text-hazard" : "border-transparent text-zinc-500 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "reviews" && (
          <ReviewsAdmin reviews={reviews} loading={loading} onAction={reviewAction} onDelete={deleteReview} />
        )}

        {tab === "bookings" && (
        <>
        {/* filters */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap gap-2">
            {["all", ...STATUSES].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`px-4 py-2 font-mono text-xs tracking-[0.15em] uppercase border transition-colors ${
                  filter === f
                    ? "bg-hazard text-black border-hazard"
                    : "border-surface-2 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={exportCompleted}
            disabled={exporting}
            data-testid="export-completed-btn"
            className="flex items-center gap-2 bg-green-500/15 text-green-300 border border-green-500/40 px-4 py-2 font-mono text-xs tracking-[0.15em] uppercase hover:bg-green-500/25 transition-colors disabled:opacity-50"
            title="Export all completed jobs to PDF"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            Export Completed (PDF)
          </button>
        </div>

        {/* list */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-hazard" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="no-bookings" className="border border-surface-2 py-20 text-center text-zinc-500 font-mono text-sm tracking-wide uppercase">
            No bookings found
          </div>
        ) : (
          <div className="space-y-px bg-surface-2 border border-surface-2">
            {filtered.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`booking-row-${b.id}`}
                className="bg-ink p-5 md:p-6"
              >
                <div className="grid md:grid-cols-[1.4fr_1.2fr_1fr_auto] gap-4 md:gap-6 md:items-center">
                <div>
                  <div className="font-display text-2xl tracking-wide">{b.name}</div>
                  <div className="font-mono text-xs text-hazard tracking-wide mt-1">{b.service}</div>
                  <div className="text-zinc-500 text-sm mt-1">{b.vehicle}</div>
                </div>
                <div className="text-sm space-y-1">
                  <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-zinc-300 hover:text-hazard">
                    <Phone size={13} /> {b.phone}
                  </a>
                  <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-zinc-300 hover:text-hazard break-all">
                    <Mail size={13} /> {b.email}
                  </a>
                  {b.notes && <div className="text-zinc-500 text-xs pt-1 italic">"{b.notes}"</div>}
                </div>
                <div className="font-mono text-xs text-zinc-400">
                  <div className="text-zinc-500 text-[10px] tracking-wider uppercase">Prefers</div>
                  <div className="text-white">{b.preferred_contact || "Phone"}</div>
                  <span className={`inline-block mt-2 px-2 py-1 border text-[10px] tracking-wider uppercase ${STATUS_STYLE[b.status] || ""}`}>
                    {b.status}
                  </span>
                </div>
                <div className="flex md:flex-col gap-2 items-start">
                  <select
                    value={b.status}
                    data-testid={`status-select-${b.id}`}
                    onChange={(e) => updateStatus(b.id, e.target.value)}
                    className="bg-surface border border-surface-2 text-white text-xs font-mono px-2 py-2 focus:outline-none focus:border-hazard"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {b.status !== "completed" && (
                    <button
                      onClick={() => remove(b.id, b.status)}
                      data-testid={`delete-btn-${b.id}`}
                      className="p-2 border border-surface-2 text-zinc-500 hover:text-red-400 hover:border-red-400/50 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                </div>

                {(b.status === "confirmed" || b.status === "completed") && (
                  <div className="mt-4 pt-4 border-t border-surface-2">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
                      Owner Notes (private)
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <textarea
                        rows={2}
                        data-testid={`owner-notes-${b.id}`}
                        value={ownerNotes[b.id] ?? b.owner_notes ?? ""}
                        onChange={(e) => setOwnerNotes((n) => ({ ...n, [b.id]: e.target.value }))}
                        placeholder="Add private notes for this job — parts used, work done, follow-ups…"
                        className="flex-1 bg-surface border border-surface-2 px-3 py-2 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-hazard resize-none"
                      />
                      <button
                        onClick={() => saveOwnerNotes(b.id)}
                        disabled={savingNotes === b.id}
                        data-testid={`save-owner-notes-${b.id}`}
                        className="flex items-center justify-center gap-2 bg-hazard text-black px-4 py-2 font-mono text-xs tracking-[0.15em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-50 h-fit"
                      >
                        {savingNotes === b.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
